import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Gauge, ChevronRight, Search } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MaturityBadge } from "@/components/AgenticVisuals";

const LEVEL_LABELS = { 1: "Ocasional", 2: "Funcional", 3: "Aumentado", 4: "Operador", 5: "Arquitecto" };
const LEVEL_FILL = { 1: "#64748b", 2: "#38bdf8", 3: "#22d3ee", 4: "#10b981", 5: "#f59e0b" };

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon size={16} strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <div className={`mt-3 font-mono text-4xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

export default function ConsultantDashboard() {
  const [q, setQ] = useState("");
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data });
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["assessments"],
    queryFn: async () => (await api.get("/assessments")).data,
  });

  const filtered = items.filter(
    (a) =>
      a.candidate_name.toLowerCase().includes(q.toLowerCase()) ||
      a.target_role.toLowerCase().includes(q.toLowerCase())
  );

  const chartData = stats
    ? Object.entries(stats.level_distribution).map(([lvl, count]) => ({
        level: `N${lvl} · ${LEVEL_LABELS[lvl]}`,
        count,
        lvl: Number(lvl),
      }))
    : [];

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Panel del consultor</h1>
        <p className="text-slate-400 mt-1.5">Evaluaciones agentic de candidatos · Talent Hackers Deputy</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <div className="relative rounded-xl border border-slate-800 bg-slate-900 p-6 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
          <div className="flex items-center gap-2 text-slate-400">
            <Gauge size={16} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-wider">Índice medio agentic</span>
          </div>
          <div className="mt-3 font-mono text-5xl font-bold text-primary">{stats?.avg_index?.toFixed(2) ?? "—"}</div>
          <div className="text-xs text-slate-500 mt-1">Agentic Productivity Index promedio</div>
        </div>
        <Stat icon={Users} label="Candidatos evaluados" value={stats?.total_candidates ?? "—"} accent="text-slate-100" />
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Distribución por nivel</div>
          <div className="h-[92px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 0, left: -28, bottom: 0 }}>
                <XAxis dataKey="lvl" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#475569", fontSize: 9 }} axisLine={false} tickLine={false} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {chartData.map((e) => (
                    <Cell key={e.lvl} fill={LEVEL_FILL[e.lvl]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
        <div className="flex items-center justify-between gap-4 p-5 border-b border-slate-800">
          <h2 className="font-heading text-xl font-medium tracking-tight">Candidatos</h2>
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <Input
              data-testid="candidate-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o puesto…"
              className="pl-9 bg-slate-950 border-slate-800 focus:border-primary"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-slate-500">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No hay candidatos evaluados todavía.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {filtered.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.03 }}
              >
                <Link
                  to={`/app/assessment/${a.id}`}
                  data-testid={`candidate-row-${a.id}`}
                  className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-slate-800/60 transition-colors duration-200"
                >
                  <div className="flex items-center gap-5 min-w-0">
                    <div className="font-mono text-2xl font-bold text-primary w-14">{a.evaluation?.agentic_index?.toFixed(2)}</div>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-100 truncate">{a.candidate_name}</div>
                      <div className="text-sm text-slate-500 truncate">{a.target_role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {a.consultant_notes?.validated && (
                      <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-500/30 rounded px-2 py-0.5">Validado</span>
                    )}
                    <MaturityBadge maturity={a.evaluation?.maturity} />
                    <ChevronRight className="text-slate-600" size={18} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
